/**
 * DefenseReviewContent — Stage 5 (Defense) composite for the accountant Case
 * Workspace: the "defend the number under scrutiny" surfaces, framed bare.
 *
 * A segmented sub-nav (approved wayfinding — active = 2px orange underline,
 * idle = dim grey, no fill) switches between three existing surfaces, each
 * rendered with showHeader=false so there's no double-chrome:
 *
 *   - Stress-Test         → <AnalysisToolsContent /> (Monte Carlo + Sensitivity)
 *   - Methodology         → <MethodologyViewContent /> (Daubert method slate)
 *   - Carrier Counter-Model → <CarrierCounterModelContent /> (rebuttal workbench)
 *
 * Zero net-new dashboards — the composite adds only the sub-nav; every panel is
 * an existing component reused via the extract-content pattern.
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, Scale, Gavel } from 'lucide-react';
import { AnalysisToolsContent } from './AnalysisToolsContent';
import { MethodologyViewContent } from '../../../pages/AccountantMethodologyView';
import { CarrierCounterModelContent } from '../CarrierCounterModelContent';

export interface DefenseReviewContentProps {
  reportId?: string;
  showHeader?: boolean;
}

type DefenseSubTab = 'stress' | 'methodology' | 'carrier';

const SUB_TABS: { id: DefenseSubTab; label: string; icon: React.ElementType }[] = [
  { id: 'stress', label: 'Stress-Test', icon: Activity },
  { id: 'methodology', label: 'Methodology', icon: Scale },
  { id: 'carrier', label: 'Carrier Counter-Model', icon: Gavel },
];

export function DefenseReviewContent({
  reportId: reportIdProp,
  showHeader = true,
}: DefenseReviewContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;
  const [subTab, setSubTab] = useState<DefenseSubTab>('stress');

  return (
    <div className="p-6 lg:p-8">
      {showHeader && (
        <div className="mb-6 max-w-[1200px] mx-auto">
          <div className="cs-eyebrow flex items-center gap-2 mb-1">
            <span>ACCOUNTANT REVIEW</span>
            <span>•</span>
            <span>DEFENSE</span>
          </div>
          <h1 className="cs-display">Defense · Stress-Test</h1>
        </div>
      )}

      {/* Segmented sub-nav — one orange accent (the active underline). */}
      <div
        role="tablist"
        aria-label="Defense sections"
        className="flex items-center gap-1 mb-6 max-w-[1200px] mx-auto"
        style={{ borderBottom: '1px solid var(--color-hairline)' }}
      >
        {SUB_TABS.map(({ id, label, icon: Icon }) => {
          const active = subTab === id;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={active}
              onClick={() => setSubTab(id)}
              data-testid={`defense-subtab-${id}`}
              className="cs-label flex items-center gap-2 px-3 py-2 rounded-[2px] transition-colors"
              style={{
                color: active ? 'var(--color-text-secondary)' : 'var(--color-text-dim)',
                borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );
        })}
      </div>

      <div data-testid={`defense-panel-${subTab}`}>
        {subTab === 'stress' && <AnalysisToolsContent reportId={reportId} showHeader={false} />}
        {subTab === 'methodology' && <MethodologyViewContent reportId={reportId} showHeader={false} />}
        {subTab === 'carrier' && <CarrierCounterModelContent reportId={reportId} showHeader={false} />}
      </div>
    </div>
  );
}

export default DefenseReviewContent;
